import { requireAdmin } from "@/lib/admin-guard";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMembership } from "@/hooks/useMembership";
import {
  adminMembersSnapshot,
  type AdminMemberRow,
} from "@/lib/admin-members.functions";
import { CountryBreakdown } from "@/components/admin/CountryBreakdown";
import { MembershipTruthPanel } from "@/components/admin/MembershipTruthPanel";
import { MembershipExpiryPanel } from "@/components/admin/MembershipExpiryPanel";
import { PAYPAL_PLAN_TERMS } from "@/lib/renewal-rate";
import { MEMBERSHIP_PLANS } from "@/lib/membership-plans";
import { resolveBillingTerm } from "@/lib/billing-term";

import {
  Loader2,
  ShieldAlert,
  RefreshCw,
  UserX,
  Clock,
  Stethoscope,
} from "lucide-react";


export const Route = createFileRoute("/admin_/members")({
  ssr: false,
  beforeLoad: requireAdmin,
  head: () => ({
    meta: [
      { title: "Admin — Members & Renewals (Recovery Trauma™)" },
      {
        name: "description",
        content:
          "Live view of Recovery Trauma™ members, cancellations, auto-renewal dates and upgrade history.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminMembersPage,
});

type Snapshot = Awaited<ReturnType<typeof adminMembersSnapshot>>;

const PLAN_LABEL: Record<string, string> = {
  monthly: "Monthly",
  six_month: "6 Months",
  sixmonth: "6 Months",
  "6month": "6 Months",
  yearly: "Yearly",
  annual: "Yearly",
};

function termName(months: number): string {
  return months === 1 ? "Monthly" : months === 12 ? "Yearly" : `${months} Months`;
}

/**
 * Billing term for a member row. The interval always comes from the real plan
 * (PayPal plan id, catalogue plan id, or the amount actually charged) — it is
 * never assumed to be monthly.
 */
function planLabel(
  planId: string | null,
  amountPaidCents?: number | null,
): string {
  const paidLabel =
    amountPaidCents != null && amountPaidCents > 0
      ? `$${(amountPaidCents / 100).toFixed(2)}`
      : null;
  if (planId) {
    const ppTerm = PAYPAL_PLAN_TERMS[planId];
    if (ppTerm) {
      return `${termName(ppTerm.months)} · ${paidLabel ?? `$${(ppTerm.cents / 100).toFixed(2)}`}`;
    }
    const plan = MEMBERSHIP_PLANS.find((p) => p.id === planId);
    if (plan) {
      const months =
        plan.interval === "year" ? plan.intervalCount * 12 : plan.intervalCount;
      return `${termName(months)} · ${paidLabel ?? `$${(plan.priceCents / 100).toFixed(2)}`}`;
    }
  }

  if (amountPaidCents != null && amountPaidCents > 0) {
    const term = resolveBillingTerm({ amount_paid_cents: amountPaidCents });
    return `${termName(term.intervalMonths)} · $${(term.cycleCents / 100).toFixed(2)}`;
  }
  if (planId) return PLAN_LABEL[planId.toLowerCase()] ?? planId;
  return "—";
}



const PROVIDER_LABEL: Record<AdminMemberRow["provider"], string> = {
  stripe: "Stripe",
  paypal: "PayPal",
  complimentary: "Complimentary",
  none: "—",
};

function fmtDate(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
}

function renewalOf(r: AdminMemberRow): string | null {
  return r.next_billing_date ?? r.current_period_end;
}

const CANCEL_REASON_LABEL: Record<string, string> = {
  too_expensive: "Too expensive",
  not_using_it: "Not using it enough",
  not_what_expected: "Not what they expected",
  found_alternative: "Found something else",
  technical_problems: "Something didn't work",
  temporary_break: "Just needed a break",
  other: "Something else",
  prefer_not_to_say: "Preferred not to say",
};

function cancelReasonCell(r: AdminMemberRow): React.ReactNode {
  if (!r.cancellation_reason) {
    return <span className="text-muted-foreground">Not shared</span>;
  }
  const label = CANCEL_REASON_LABEL[r.cancellation_reason] ?? r.cancellation_reason;
  return (
    <span title={r.cancellation_reason_detail ?? undefined}>
      {label}
      {r.cancellation_reason_detail ? (
        <span className="block max-w-64 truncate text-xs text-muted-foreground">
          “{r.cancellation_reason_detail}”
        </span>
      ) : null}
    </span>
  );
}

function inactivityCell(r: AdminMemberRow): React.ReactNode {
  const d = r.days_since_login;
  if (d === null) return <span className="text-muted-foreground">No record</span>;
  const tone =
    d >= 30 ? "text-destructive" : d >= 14 ? "text-orange-600" : "text-muted-foreground";
  return <span className={tone}>{d === 0 ? "Today" : `${d}d`}</span>;
}


function StatCard({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  hint?: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-muted-foreground">
        {icon}
        <span className="text-xs uppercase tracking-wide">{label}</span>
      </div>
      <p className="mt-2 font-serif text-3xl text-foreground">{value}</p>
      {hint ? (
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

function AdminMembersPage() {
  const { loading: memLoading, isAdmin } = useMembership();
  const snapshotFn = useServerFn(adminMembersSnapshot);
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setBusy(true);
    setError(null);
    try {
      setSnap(await snapshotFn({ data: undefined as never }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load members.");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (isAdmin) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (memLoading) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-4xl items-center justify-center px-4">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        <ShieldAlert className="mx-auto h-8 w-8 text-amber-600" />
        <p className="mt-3 text-sm">Admin access required.</p>
        <Link to="/" className="mt-4 inline-block text-sm underline">
          Back to home
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <div className="mb-6"><CountryBreakdown /></div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl text-foreground">
            Members &amp; renewals
          </h1>
          <p className="mt-1 text-xs text-muted-foreground">
            Live from the membership records. Read-only — nothing here changes a
            subscription.
            {snap
              ? ` Updated ${new Date(snap.generatedAt).toLocaleTimeString("en-GB")}.`
              : ""}
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-sm"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {error ? (
        <p className="mb-6 rounded-xl border border-border bg-card p-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {snap === null ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* One membership calculation, shared with the main dashboard. */}
          <MembershipTruthPanel />


          {/* --- Therapists / practitioners: separate category --- */}
          <Section title={`Therapists / practitioners (${snap.stats.therapistsTotal})`}>
            <div className="mb-3 grid gap-3 sm:grid-cols-3">
              <StatCard
                icon={<Stethoscope className="h-4 w-4" />}
                label="Active"
                value={snap.stats.therapistsActive}
                hint="Listed in the directory"
              />
              <StatCard
                icon={<Stethoscope className="h-4 w-4" />}
                label="Pending approval"
                value={snap.stats.therapistsPending}
                hint="In review or awaiting publication"
              />
              <StatCard
                icon={<Stethoscope className="h-4 w-4" />}
                label="Inactive"
                value={snap.stats.therapistsInactive}
                hint="Rejected or no longer listed"
              />
            </div>
            <Table
              head={["Name", "Email", "Category", "Status"]}
              rows={snap.therapists.map((t) => [
                t.name ?? "—",
                t.email ?? "—",
                t.bucket === "active"
                  ? "Active"
                  : t.bucket === "pending"
                    ? "Pending approval"
                    : "Inactive",
                t.status.replace(/_/g, " "),
              ])}
              keys={snap.therapists.map((t) => t.id)}
              empty="No therapist or practitioner records."
            />
          </Section>

          <div className="mt-8 grid gap-3 sm:grid-cols-3">
            <StatCard
              icon={<UserX className="h-4 w-4" />}
              label="Never returned"
              value={snap.stats.neverReturned}
              hint="Signed in once at signup only"
            />
            <StatCard
              icon={<Clock className="h-4 w-4" />}
              label="No login 14+ days"
              value={snap.stats.inactive14}
              hint="Members quiet for two weeks or more"
            />
            <StatCard
              icon={<Clock className="h-4 w-4" />}
              label="No login 30+ days"
              value={snap.stats.inactive30}
              hint="Members quiet for a month or more"
            />
          </div>


          <Section title={`Cancellations (${snap.cancellations.length})`}>
            <Table
              head={["Email", "Plan", "Provider", "Cancelled", "Access until", "Reason"]}
              rows={snap.cancellations.map((r) => [
                r.email ?? "—",
                planLabel(r.plan_id, r.amount_paid_cents),
                PROVIDER_LABEL[r.provider],
                fmtDate(r.cancelled_at),
                fmtDate(r.current_period_end),
                cancelReasonCell(r),
              ])}
              keys={snap.cancellations.map((r) => r.user_id)}
              empty="No cancellations recorded."
            />
          </Section>

          <Section title={`Never returned after signup (${snap.neverReturned.length})`}>
            <Table
              head={["Email", "Plan", "Status", "Signed up", "Only login", "Days since", "Next renewal"]}
              rows={snap.neverReturned.map((r) => [
                r.email ?? "—",
                planLabel(r.plan_id, r.amount_paid_cents),
                r.status,
                fmtDate(r.activated_at ?? r.paid_at),
                fmtDate(r.last_login_at),
                inactivityCell(r),
                fmtDate(renewalOf(r)),
              ])}
              keys={snap.neverReturned.map((r) => r.user_id)}
              empty="Everyone has come back at least once."
            />
          </Section>

          <Section title={`No login for 14+ days (${snap.inactive14.length})`}>
            <Table
              head={["Email", "Plan", "Provider", "Last login", "Days since", "Next renewal"]}
              rows={snap.inactive14.map((r) => [
                r.email ?? "—",
                planLabel(r.plan_id, r.amount_paid_cents),
                PROVIDER_LABEL[r.provider],
                fmtDate(r.last_login_at),
                inactivityCell(r),
                fmtDate(renewalOf(r)),
              ])}
              keys={snap.inactive14.map((r) => r.user_id)}
              empty="No quiet members."
            />
          </Section>

          <Section title={`No login for 30+ days (${snap.inactive30.length})`}>
            <Table
              head={["Email", "Plan", "Provider", "Last login", "Days since", "Next renewal"]}
              rows={snap.inactive30.map((r) => [
                r.email ?? "—",
                planLabel(r.plan_id, r.amount_paid_cents),
                PROVIDER_LABEL[r.provider],
                fmtDate(r.last_login_at),
                inactivityCell(r),
                fmtDate(renewalOf(r)),
              ])}
              keys={snap.inactive30.map((r) => r.user_id)}
              empty="No members quiet for a month."
            />
          </Section>


          <Section title={`Upgrade history (${snap.upgrades.length})`}>
            <Table
              head={[
                "Email",
                "From",
                "To",
                "Provider",
                "Upgraded",
                "Next renewal",
              ]}
              rows={snap.upgrades.map((r) => [
                r.email ?? "—",
                planLabel(r.upgraded_from_plan_id, null),
                planLabel(r.plan_id, r.amount_paid_cents),
                r.upgrade_provider
                  ? r.upgrade_provider === "paypal"
                    ? "PayPal"
                    : "Stripe"
                  : PROVIDER_LABEL[r.provider],
                fmtDate(r.upgraded_at),
                fmtDate(renewalOf(r)),
              ])}
              keys={snap.upgrades.map((r) => r.user_id)}
              empty="No upgrades recorded yet."
            />
          </Section>

          <MembershipExpiryPanel />



        </>
      )}
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-8">
      <h2 className="mb-2 font-serif text-lg text-foreground">{title}</h2>
      {children}
    </section>
  );
}

function Table({
  head,
  rows,
  keys,
  empty,
}: {
  head: string[];
  rows: React.ReactNode[][];
  keys: string[];
  empty: string;
}) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-border bg-card">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-start text-xs uppercase text-muted-foreground">
          <tr>
            {head.map((h) => (
              <th key={h} className="whitespace-nowrap px-3 py-2">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={head.length}
                className="px-3 py-6 text-center text-muted-foreground"
              >
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((cells, i) => (
              <tr key={keys[i]} className="border-t border-border/50">
                {cells.map((c, j) => (
                  <td key={j} className="whitespace-nowrap px-3 py-2">
                    {c}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
